import { type FC, useMemo } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { Table, Tag, Button, Popconfirm, Tooltip, message } from 'antd';
import { Edit, Globe, Archive, FileText, ExternalLink, Languages } from 'lucide-react';
import dayjs from 'dayjs';

export interface BlogArticle {
  id: string;
  title: string;
  slug: string;
  status: string;
  author: string;
  excerpt: string | null;
  wordCount: number;
  readTimeMinutes: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  content?: string;
  locale?: string;
  featuredImageUrl?: string | null;
}

const LANDING_URL = import.meta.env.VITE_LANDING_URL || 'https://feedbackorchestrator.com';

interface BlogListResponse {
  articles: BlogArticle[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  draft: { color: 'default', label: 'Brouillon' },
  review: { color: 'blue', label: 'En review' },
  published: { color: 'green', label: 'Publié' },
  archived: { color: 'orange', label: 'Archivé' },
};

interface BlogArticleTableProps {
  onArticleClick: (article: BlogArticle) => void;
  onNewArticle: () => void;
}

const BlogArticleTable: FC<BlogArticleTableProps> = ({ onArticleClick, onNewArticle }) => {
  const invalidate = useInvalidate();
  const { mutate: publishArticle } = useCustomMutation();
  const { mutate: archiveArticle } = useCustomMutation();

  const { data, isLoading } = useCustom<BlogListResponse>({
    url: '/api/v1/admin/marketing/blog',
    method: 'get',
    config: { query: { page: 1, limit: 100 } },
  });

  const articles = data?.data?.articles ?? [];

  // Build a slug→articles map to find translated pairs
  const translationMap = useMemo(() => {
    const map = new Map<string, BlogArticle[]>();
    for (const a of articles) {
      const key = a.slug;
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return map;
  }, [articles]);

  function getTranslatedArticle(article: BlogArticle): BlogArticle | undefined {
    const siblings = translationMap.get(article.slug) ?? [];
    return siblings.find((s) => s.id !== article.id && s.locale !== article.locale);
  }

  const handlePublish = (id: string) => {
    publishArticle(
      { url: `/api/v1/admin/marketing/blog/${id}/publish`, method: 'post', values: {} },
      {
        onSuccess: (res) => {
          const translated = res?.data?.translatedPublishedId;
          if (translated) {
            message.success('Article publié (+ version traduite)');
          } else {
            message.success('Article publié');
          }
          invalidate({ invalidates: ['all'] });
        },
        onError: (err) => message.error(err?.message || 'Erreur lors de la publication'),
      },
    );
  };

  const handleArchive = (id: string) => {
    archiveArticle(
      { url: `/api/v1/admin/marketing/blog/${id}`, method: 'patch', values: { status: 'archived' } },
      {
        onSuccess: () => {
          message.success('Article archivé');
          invalidate({ invalidates: ['all'] });
        },
        onError: () => message.error("Erreur lors de l'archivage"),
      },
    );
  };

  const columns = [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, record: BlogArticle) => {
        const translated = getTranslatedArticle(record);
        return (
          <div>
            <span className="font-medium text-slate-900 dark:text-white">{title}</span>
            {translated && (
              <div className="mt-0.5">
                <button
                  onClick={(e) => { e.stopPropagation(); onArticleClick(translated); }}
                  className="inline-flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-600 dark:text-blue-400"
                >
                  <Languages className="h-3 w-3" />
                  {translated.locale?.toUpperCase()} : {translated.title.slice(0, 40)}{translated.title.length > 40 ? '…' : ''}
                </button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Locale',
      dataIndex: 'locale',
      key: 'locale',
      width: 70,
      render: (locale: string | undefined) => (
        <Tag color={locale === 'en' ? 'geekblue' : 'cyan'}>{(locale ?? 'fr').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const s = STATUS_MAP[status] ?? { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Publication',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      width: 120,
      render: (d: string | null) => (d ? dayjs(d).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Mots-clés SEO',
      dataIndex: 'seoKeywords',
      key: 'seoKeywords',
      width: 200,
      render: (kw: string[] | undefined) => {
        if (!kw?.length) return <span className="text-slate-400">—</span>;
        const visible = kw.slice(0, 3);
        const extra = kw.length - 3;
        return (
          <div className="flex flex-wrap gap-1">
            {visible.map((k) => (
              <Tag key={k} color="blue" className="text-[10px]">{k}</Tag>
            ))}
            {extra > 0 && <Tag className="text-[10px]">+{extra}</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Lecture',
      dataIndex: 'readTimeMinutes',
      key: 'readTimeMinutes',
      width: 80,
      render: (m: number) => `${m}min`,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: BlogArticle) => {
        const previewUrl = `${LANDING_URL}/${record.locale ?? 'fr'}/blog/${record.slug}`;
        return (
          <div className="flex items-center gap-1">
            <Tooltip title="Modifier">
              <Button type="text" size="small" icon={<Edit className="h-3.5 w-3.5" />} onClick={() => onArticleClick(record)} />
            </Tooltip>
            {record.status === 'published' && (
              <Tooltip title="Voir sur le blog">
                <Button type="text" size="small" icon={<ExternalLink className="h-3.5 w-3.5 text-blue-500" />}
                  onClick={() => window.open(previewUrl, '_blank')} />
              </Tooltip>
            )}
            {record.status !== 'published' && (
              <Tooltip title="Publier (+ version traduite)">
                <Button type="text" size="small" icon={<Globe className="h-3.5 w-3.5 text-emerald-500" />} onClick={() => handlePublish(record.id)} />
              </Tooltip>
            )}
            {record.status !== 'archived' && (
              <Popconfirm title="Archiver cet article ?" onConfirm={() => handleArchive(record.id)} okText="Oui" cancelText="Non">
                <Tooltip title="Archiver">
                  <Button type="text" size="small" icon={<Archive className="h-3.5 w-3.5 text-orange-500" />} />
                </Tooltip>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="fc-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Articles</h2>
        <Button type="primary" size="small" icon={<FileText className="h-3.5 w-3.5" />} onClick={onNewArticle}>
          Nouvel article
        </Button>
      </div>
      <Table
        dataSource={articles}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 10, size: 'small' }}
        size="small"
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default BlogArticleTable;
